/**
 * MVP-11 #32 — the guest's stale status card ("pins forever across reloads"). `RequestPage`
 * always shows `myRequests(deviceId)[0]` — this device's NEWEST request, whatever its age. A
 * `deviceId` is itself remembered forever in `localStorage` (lib/deviceId.ts), so a guest who
 * used this page once weeks ago and never asks again keeps seeing that same old rejected/expired
 * card on every future visit, with no way to make it go away.
 *
 * Rather than a growing list (which would need its own eviction policy), this remembers only the
 * ONE most recently dismissed `requestId`. The moment a genuinely different request becomes this
 * device's newest (a new ask, or the KJ deciding a different one), the stored id no longer
 * matches and `isRequestDismissed` naturally starts returning false — the old record is "dropped"
 * by simply ceasing to apply, with no explicit TTL/cleanup needed. Same
 * localStorage-with-in-memory-fallback shape as `lib/deviceId.ts` (private to this module — see
 * that file and `api/guest.ts`'s own doc comment for why the guest page never shares storage
 * helpers with the operator console's `api/client.ts`).
 */

const DISMISSED_KEY = 'mvcp.dismissedRequestId';

const memoryStore = new Map<string, string>();

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function storageGet(key: string): string | null {
  if (hasLocalStorage()) {
    try {
      return localStorage.getItem(key);
    } catch {
      /* private-mode/storage-disabled browsers: fall through to memory */
    }
  }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string): void {
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.set(key, value);
}

/** True once `dismissRequest(requestId)` has been called for THIS exact request and nothing
 * newer has been dismissed since. */
export function isRequestDismissed(requestId: string): boolean {
  const raw = storageGet(DISMISSED_KEY);
  if (!raw) return false;
  try {
    const record = JSON.parse(raw) as { requestId?: string };
    return record.requestId === requestId;
  } catch {
    return false;
  }
}

export function dismissRequest(requestId: string): void {
  storageSet(DISMISSED_KEY, JSON.stringify({ requestId, dismissedAt: Date.now() }));
}
